import { Box } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import type { ReactNode } from "react";

/**
 * Responsive section container with optimal widths and paddings
 *
 * Padding scale (vertical):
 * - Mobile: 48px (py: 6)
 * - Tablet: 64px (py: 8)
 * - Desktop: 80-96px (py: 10-12)
 *
 * Padding scale (horizontal):
 * - Mobile (< 600px): 16px
 * - Small Tablet (600-768px): 24px
 * - Large Tablet (768-900px): 32px
 * - Small Desktop (900-1200px): 48px
 * - Large Desktop (> 1200px): centered with max-width
 */

type SectionVariant = "default" | "hero" | "feature" | "cta" | "full-bleed";

interface LandingSectionProps {
  children: ReactNode;
  variant?: SectionVariant;
  /** Background color - use Joy UI color tokens */
  bgcolor?: string;
  /** Additional sx props */
  sx?: SxProps;
  /** HTML id for anchor links */
  id?: string;
}

const getVariantStyles = (variant: SectionVariant): SxProps => {
  const baseStyles: SxProps = {
    py: { xs: 6, sm: 8, md: 10 },
  };

  switch (variant) {
    case "hero":
      return {
        ...baseStyles,
        py: { xs: 8, sm: 10, md: 12, lg: 16 },
        minHeight: { xs: "auto", md: "calc(100vh - 88px)" },
        display: "flex",
        alignItems: "center",
      };
    case "feature":
      return {
        ...baseStyles,
        py: { xs: 8, sm: 10, md: 12, lg: 14 },
      };
    case "cta":
      return {
        ...baseStyles,
        py: { xs: 10, sm: 12, md: 14, lg: 16 },
        textAlign: "center",
      };
    case "full-bleed":
      return {
        ...baseStyles,
        px: 0,
      };
    default:
      return baseStyles;
  }
};

export const LandingSection = ({
  children,
  variant = "default",
  bgcolor,
  sx,
  id,
}: LandingSectionProps) => {
  const variantStyles = getVariantStyles(variant);

  return (
    // @ts-expect-error Weird TS error here, but it works fine.
    <Box
      component="section"
      id={id}
      sx={{
        width: "100%",
        bgcolor: bgcolor || "transparent",
        ...variantStyles,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: {
            xs: "100%",
            sm: "100%",
            md: "100%",
            lg: 1400,
            xl: 1400,
          },
          mx: "auto",
          px: {
            xs: 2, // 16px - Mobile
            sm: 3, // 24px - Small tablet
            md: 4, // 32px - Large tablet
            lg: 6, // 48px - Small desktop (before max-width kicks in)
            xl: 0, // 0px - Large desktop (max-width handles it)
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
